/**
 * The photo prompt deck lives in src/client/prompts/<category>/. Each image in a category
 * folder is a "recreate this" prompt, and each line of a category's prompts.txt is a plain
 * photo prompt. Vite reads the folder at build time, so adding prompts is just adding files.
 */
const images = import.meta.glob(
  './prompts/*/*.{jpg,jpeg,jfif,png,webp,gif,JPG,JPEG,JFIF,PNG,WEBP,GIF}',
  {
    eager: true,
    query: '?url',
    import: 'default',
  },
) as Record<string, string>;
const promptFiles = import.meta.glob('./prompts/*/prompts.txt', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

export interface DeckPrompt {
  key: string;
  category: string;
  name: string;
  description: string;
  referenceImage: string | null;
}

const titleCase = (value: string) =>
  value
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const slug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** "./prompts/the-squad/prompts.txt" -> ["the-squad", "prompts.txt"] */
const parts = (path: string) => path.replace('./prompts/', '').split('/') as [string, string];

/** Turns one line of prompts.txt into a prompt: "Name: what to do" or just "What to do". */
export function parsePromptLine(line: string) {
  const split = line.indexOf(': ');
  const name = (split > 0 ? line.slice(0, split) : line).trim().slice(0, 100);
  const description = split > 0 ? line.slice(split + 2).trim() : '';
  return { name, description };
}

export function loadDeck(): DeckPrompt[] {
  const prompts: DeckPrompt[] = [];
  for (const path of Object.keys(images).sort()) {
    const [folder, file] = parts(path);
    prompts.push({
      key: `${folder}/${file}`,
      category: titleCase(folder),
      name: 'Recreate Meme!',
      description: 'Recreate this picture to your best effort!',
      referenceImage: `${folder}/${file}`,
    });
  }
  for (const [path, text] of Object.entries(promptFiles).sort()) {
    const [folder] = parts(path);
    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line || line.startsWith('#')) continue;
      const { name, description } = parsePromptLine(line);
      if (name.length < 2) continue;
      prompts.push({
        key: `${folder}/${slug(name)}`,
        category: titleCase(folder),
        name,
        description,
        referenceImage: null,
      });
    }
  }
  return prompts;
}

/** The built URL of a reference image, or undefined if the file has been removed. */
export function referenceImageUrl(key: string | null | undefined) {
  return key ? images[`./prompts/${key}`] : undefined;
}
